import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { type AxiosError } from 'axios'

import {
  type UpdateResponse,
  type ErrorResponse,
  type SurveySubmitRequest,
  type WorkspaceStats,
  type Dday,
  type TodayStats,
  type ContentStats,
} from '../../api/interfaces/sconnStudyData'
import { type DateRange } from '../../util/CustomDate'

import { useSconnStudyDataApi } from './context'

export const useLearningKeywords = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['learningKeywords'],
    queryFn: async () => {
      const result = await sconnStudyData.getLearningKeywords()
      return result
    },
    staleTime: 1000 * 60 * 60 * 24,
  })

  return { data, isPending, isFetching }
}

export const useConsentSurvey = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, boolean>({
    mutationFn: async (allow: boolean) => {
      const result = await sconnStudyData.postConsentSurvey(allow)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  return { isPending, mutate }
}

export const useUserInformation = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data } = useSuspenseQuery({
    queryKey: ['user'],
    queryFn: async () => await sconnStudyData.getUserInformation(),
    refetchOnWindowFocus: false,
  })

  return { data }
}

export const useUserDday = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data } = useSuspenseQuery({
    queryKey: ['dday'],
    queryFn: async () => {
      const result = await sconnStudyData.getUserDday()
      return result ?? null
    },
    refetchOnWindowFocus: false,
  })

  return { data }
}

export const useUpdateUserDday = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, Dday>({
    mutationFn: async (dday: Dday) => {
      const result = await sconnStudyData.postUserDday(dday)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dday'] })
    },
  })

  return { isPending, mutate }
}

export const useUpdateNextGoalOfDays = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, number>({
    mutationFn: async (seconds: number) => {
      const timezoneOffset = new Date().getTimezoneOffset()
      const result = await sconnStudyData.postGoalDaily(seconds, timezoneOffset)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statistics', 'user', 'today'] })
    },
  })

  return { isPending, mutate }
}

export const useUpdateCurrentGoalOfDays = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, number>({
    mutationFn: async (seconds: number) => {
      const timezoneOffset = new Date().getTimezoneOffset()
      const result = await sconnStudyData.putGoalDaily(seconds, timezoneOffset)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statistics', 'user', 'today'] })
    },
  })

  return { isPending, mutate }
}

export const useUserSurvey = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data } = useQuery({
    queryKey: ['survey'],
    queryFn: async () => {
      const result = await sconnStudyData.getUserSurvey()
      return result
    },
  })

  return { data }
}

export const useUserSuggests = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data } = useSuspenseQuery({
    queryKey: ['suggests', 'user'],
    queryFn: async () => {
      const result = await sconnStudyData.getUserSuggests()
      return result
    },
    refetchOnWindowFocus: false,
  })

  return { data }
}

export const useUserTodayStatistics = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data, isPending } = useSuspenseQuery({
    queryKey: ['statistics', 'user', 'today'],
    queryFn: async () => {
      const timezoneOffset = new Date().getTimezoneOffset()

      const result = await sconnStudyData.getUserStatistics({
        target: 'today',
        offset: `${timezoneOffset}`,
      })
      return result
    },
    refetchOnWindowFocus: false,
  })

  return { data: data as TodayStats, isPending }
}

export const useUserWorkspaceStatistics = ({ from, to }: DateRange) => {
  const { sconnStudyData } = useSconnStudyDataApi()
  const today = new Date()
  const staleTime = to.isSame(today) ? 0 : 1000 * 60 * 60 * 24
  const fromStr = from.toYearMonthDay()
  const toStr = to.toYearMonthDay()

  const { data } = useSuspenseQuery({
    queryKey: ['statistics', 'user', 'workspace', fromStr, toStr],
    queryFn: async () => {
      const timezoneOffset = new Date().getTimezoneOffset()
      const result = await sconnStudyData.getUserStatistics({
        target: 'workspace',
        from: fromStr,
        to: toStr,
        offset: `${timezoneOffset}`,
      })
      return result
    },
    staleTime,
    refetchOnWindowFocus: false,
  })

  return { data: data as WorkspaceStats }
}

export const useUserContentStatistics = ({ from, to }: DateRange, disable: boolean) => {
  const { sconnStudyData } = useSconnStudyDataApi()
  const today = new Date()
  const staleTime = to.isSame(today) ? 0 : 1000 * 60 * 60 * 24
  const fromStr = from.toYearMonthDay()
  const toStr = to.toYearMonthDay()

  const { data } = useSuspenseQuery({
    queryKey: ['statistics', 'user', 'content', fromStr, toStr],
    queryFn: async () => {
      if (disable) return 'NO_DATA'

      const timezoneOffset = new Date().getTimezoneOffset()
      const result = await sconnStudyData.getUserStatistics({
        target: 'content',
        from: fromStr,
        to: toStr,
        offset: `${timezoneOffset}`,
      })
      return result
    },
    staleTime,
    refetchOnWindowFocus: false,
  })

  return { data: data as ContentStats }
}

export const useUpdateNickname = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, string>({
    mutationFn: async (nickname: string) => {
      const result = await sconnStudyData.postNickname(nickname)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })

  return { isPending, mutate }
}

export const usePostSurvey = () => {
  const queryClient = useQueryClient()

  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<
    UpdateResponse,
    AxiosError<ErrorResponse>,
    SurveySubmitRequest
  >({
    mutationFn: async (surveyResult: SurveySubmitRequest) => {
      const result = await sconnStudyData.postSurvey(surveyResult)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: ['survey'] })
    },
    retry: 0,
  })

  return { isPending, mutate }
}

export const useValidateNickname = () => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { isPending, mutate } = useMutation<UpdateResponse, AxiosError<ErrorResponse>, string>({
    mutationFn: async (nickname: string) => {
      const result = await sconnStudyData.validateNickname(nickname)
      return result
    },
  })

  return { isPending, mutate }
}

export const useSearchExams = (searchWord: string) => {
  const { sconnStudyData } = useSconnStudyDataApi()

  const { data, isFetching } = useQuery({
    queryKey: ['exams', 'search', searchWord],
    queryFn: async () => await sconnStudyData.searchExams(searchWord),
    enabled: searchWord.trim().length > 0,
    staleTime: 1000 * 60 * 60 * 24,
  })

  return { data, isFetching }
}
